import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../components/Modal.jsx';
import { sitesApi } from '../api/sites.js';
import { apiError } from '../api/client.js';
import { DEFAULT_CANVAS_THEME } from './ThemeContext.jsx';

const FONT_PRESETS = [
  { value: 'Inter, system-ui, sans-serif', label: 'Inter (default)' },
  { value: '"IBM Plex Sans", system-ui, sans-serif', label: 'IBM Plex Sans' },
  { value: '"DM Sans", system-ui, sans-serif', label: 'DM Sans' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia (serif)' },
  { value: '"JetBrains Mono", ui-monospace, monospace', label: 'JetBrains Mono' },
];

export default function ThemeModal({ open, onClose, site, onSaved }) {
  const [theme, setTheme] = useState(DEFAULT_CANVAS_THEME);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTheme({
      ...DEFAULT_CANVAS_THEME,
      ...(site?.theme || {}),
      colors: { ...DEFAULT_CANVAS_THEME.colors, ...((site?.theme || {}).colors || {}) },
    });
  }, [open, site]);

  function setColor(key, value) {
    setTheme((t) => ({ ...t, colors: { ...t.colors, [key]: value } }));
  }

  async function save() {
    if (!site) return;
    setBusy(true);
    try {
      const updated = await sitesApi.update(site.id, { theme });
      onSaved?.(updated);
      toast.success('Theme saved');
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Site theme"
      wide
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save theme'}
          </button>
        </>
      }
    >
      <p className="mb-5 text-sm text-ink-500">
        Theme tokens drive the default colors of every block on this site — including buttons,
        section backgrounds, and link CTAs. Tweak here, see the canvas update live.
      </p>

      <section className="mb-6">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">
          Colors
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <ColorRow label="Primary" value={theme.colors.primary} onChange={(v) => setColor('primary', v)} />
          <ColorRow label="Background" value={theme.colors.background} onChange={(v) => setColor('background', v)} />
          <ColorRow label="Foreground" value={theme.colors.foreground} onChange={(v) => setColor('foreground', v)} />
          <ColorRow label="Muted" value={theme.colors.muted} onChange={(v) => setColor('muted', v)} />
        </div>
      </section>

      <section className="mb-2">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">
          Typography
        </h3>
        <div>
          <label className="label">Body font</label>
          <select
            className="input"
            value={theme.fontFamily}
            onChange={(e) => setTheme((t) => ({ ...t, fontFamily: e.target.value }))}
          >
            {FONT_PRESETS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-500">
            Applied at the public site root and inherited by every block.
          </p>
        </div>
      </section>
    </Modal>
  );
}

function ColorRow({ label, value, onChange }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 cursor-pointer rounded border border-ink-200"
        />
        <input
          className="input flex-1 font-mono text-xs"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}
